import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Award } from "lucide-react";
import { ROUTES } from "@/lib/routes";

interface AchievementEarnedCardProps {
  activity: {
    id: string;
    timestamp: string;
    user?: {
      id: string;
      username: string;
      avatar_url?: string;
    };
    metadata?: {
      achievement_name?: string;
      achievement_description?: string;
      run_id?: string;
    };
  };
}

export const AchievementEarnedCard = ({ activity }: AchievementEarnedCardProps) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (activity.metadata?.run_id) {
      navigate(ROUTES.run(activity.metadata.run_id));
    } else if (activity.user?.id) {
      navigate(ROUTES.profile(activity.user.id));
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activity.user?.id) {
      navigate(ROUTES.profile(activity.user.id));
    }
  };

  const username = activity.user?.username || "User";
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleCardClick}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* User Avatar with Achievement Badge */}
          <div className="relative flex-shrink-0" onClick={handleUserClick}>
            <Avatar className="h-12 w-12">
              <AvatarImage src={activity.user?.avatar_url} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 h-6 w-6 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center border-2 border-background">
              <Award className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-semibold">{username}</span> earned an achievement
            </p>
            
            {activity.metadata?.achievement_name && (
              <div className="mt-2 p-3 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:to-amber-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
                  <p className="font-semibold text-sm text-yellow-900 dark:text-yellow-100">
                    {activity.metadata.achievement_name}
                  </p>
                </div>
                {activity.metadata?.achievement_description && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    {activity.metadata.achievement_description}
                  </p>
                )}
              </div>
            )}
            
            <p className="text-xs text-muted-foreground mt-2">
              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
